import {
  InvoiceTransformationAvailableField,
  InvoiceTransformationConditionOperatorEnum,
  InvoiceTransformationRequestUi,
} from '@monetize/types/app';
import { FC } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { IoMdTrash } from 'react-icons/io';
import {
  MCustomIconButton,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MText,
} from '../Monetize';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';
import { MainSearchInputV2 } from '../Monetize/MCustomSelect/components/MainSearchInputV2';
import { InvoiceTransformationConditionValue } from './InvoiceTransformationConditionValue';
import {
  getFieldKind,
  isMultiValueOperator,
  OPERATOR_OPTIONS,
} from './invoiceTransformation.utils';

interface InvoiceTransformationConditionProps {
  lineRuleIndex: number;
  groupIndex: number;
  conditionIndex: number;
  availableFields: InvoiceTransformationAvailableField[];
  isUsageEvent: boolean;
  formContext: UseFormReturn<InvoiceTransformationRequestUi>;
  isReadOnly: boolean;
  canRemove: boolean;
  showLabels: boolean;
  onRemove: () => void;
}

export const InvoiceTransformationCondition: FC<
  InvoiceTransformationConditionProps
> = ({
  lineRuleIndex,
  groupIndex,
  conditionIndex,
  availableFields,
  isUsageEvent,
  formContext,
  isReadOnly,
  canRemove,
  showLabels,
  onRemove,
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = formContext;

  const prefix =
    `invoiceLineRules.${lineRuleIndex}.conditionGroups.${groupIndex}.conditions.${conditionIndex}` as const;

  const operator = watch(`${prefix}.operator`);
  const fieldPath = watch(`${prefix}.field`);
  // Usage event values are always free-text strings — no reference-picker
  // treatment, regardless of the property name.
  const fieldKind = isUsageEvent ? 'STRING' : getFieldKind(fieldPath);
  const isMulti = isMultiValueOperator(operator);

  const conditionErrors =
    errors.invoiceLineRules?.[lineRuleIndex]?.conditionGroups?.[groupIndex]
      ?.conditions?.[conditionIndex];

  const handleFieldChange = (nextFieldPath: string) => {
    const previousKind = isUsageEvent ? 'STRING' : getFieldKind(fieldPath);
    const nextKind = isUsageEvent ? 'STRING' : getFieldKind(nextFieldPath);
    setValue(`${prefix}.field`, nextFieldPath, { shouldDirty: true });
    if (previousKind !== nextKind) {
      setValue(`${prefix}.value`, '', { shouldDirty: true });
      setValue(`${prefix}.values`, [], { shouldDirty: true });
    }
  };

  const handleOperatorChange = (
    next: InvoiceTransformationConditionOperatorEnum,
  ) => {
    const wasMulti = isMultiValueOperator(operator);
    const nowMulti = isMultiValueOperator(next);
    setValue(`${prefix}.operator`, next, { shouldDirty: true });
    if (wasMulti !== nowMulti) {
      setValue(`${prefix}.value`, '', { shouldDirty: true });
      setValue(`${prefix}.values`, [], { shouldDirty: true });
    }
  };

  return (
    <MGrid templateColumns="40px 1fr auto" gap={2} alignItems="end" mb={1}>
      <MGridItem alignSelf="end" pb={2}>
        <MText fontWeight="semibold" color="tGray.darkPurple">
          {conditionIndex === 0 ? '' : 'AND'}
        </MText>
      </MGridItem>
      <MGridItem>
        <MGrid templateColumns="2fr 1fr 2fr" gap={2}>
          <MFormField
            error={conditionErrors?.field}
            label={showLabels ? 'Field' : undefined}
            isRequired
          >
            <Controller
              name={`${prefix}.field`}
              control={control}
              render={({ field: { onChange: _onChange, value, ...rest } }) =>
                isUsageEvent ? (
                  <MInput
                    placeholder="Custom property name"
                    value={value ?? ''}
                    onChange={(event) => handleFieldChange(event.target.value)}
                    isDisabled={isReadOnly}
                    {...rest}
                  />
                ) : (
                  <MCustomSelect
                    placeholder="Field"
                    useMainInputAsSearchInput
                    MainInputComponent={MainSearchInputV2}
                    items={availableFields}
                    itemTitle="description"
                    itemValue="fieldPath"
                    useHighlightedItemList
                    value={value}
                    onChange={(next) => handleFieldChange(next as string)}
                    isDisabled={isReadOnly}
                    {...rest}
                  />
                )
              }
            />
          </MFormField>
          <MFormField
            error={conditionErrors?.operator}
            label={showLabels ? 'Operator' : undefined}
            isRequired
          >
            <Controller
              name={`${prefix}.operator`}
              control={control}
              render={({ field: { onChange: _onChange, ...rest } }) => (
                <MCustomSelect
                  items={OPERATOR_OPTIONS}
                  itemTitle="title"
                  itemValue="value"
                  onChange={(next) =>
                    handleOperatorChange(
                      next as InvoiceTransformationConditionOperatorEnum,
                    )
                  }
                  isDisabled={isReadOnly}
                  {...rest}
                />
              )}
            />
          </MFormField>
          <MFormField
            error={isMulti ? conditionErrors?.values : conditionErrors?.value}
            label={showLabels ? 'Value' : undefined}
            isRequired
          >
            <InvoiceTransformationConditionValue
              prefix={prefix}
              formContext={formContext}
              fieldKind={fieldKind}
              operator={operator}
              isDisabled={isReadOnly}
            />
          </MFormField>
        </MGrid>
      </MGridItem>
      <MGridItem alignSelf="end" pb={1}>
        {!isReadOnly && (
          <MCustomIconButton
            variant="icon"
            icon={IoMdTrash}
            onClick={onRemove}
            isDisabled={!canRemove}
            iconColor={canRemove ? 'tRed.base' : 'tGray.back'}
            aria-label="Remove condition"
          />
        )}
      </MGridItem>
    </MGrid>
  );
};
